// Correction Center — Phase P1 shell (docs/CORRECTION-CENTER-P2B-DESIGN.md).
// The HANDS of the system: the only place financial records change, always
// minting COR paper. This phase ships the wizard frame, the thin registry
// and the manager's "Flags under your management" list; the payment
// correction types go live in Phase P2 (each wing wires into the wizard).
// Full visual design pass is Phase P5 (owner-approved: mockup first).
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth, api } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  FilePenLine, ListChecks, Flag, Clock, ShieldCheck, Banknote,
  RefreshCw, Upload, ArrowRight, Lock, Search, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const TIER_BADGE = {
  open_day: { label: 'Open day', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  closed_day: { label: 'Closed day', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  sealed_void: { label: 'Sealed — owner tier', cls: 'bg-red-100 text-red-800 border-red-200' },
};

// Tile categories (owner-approved 2026-09-02): grouped by the sentence in
// the manager's head — remove vs fix vs checks vs charges — so the right
// door is findable at a glance.
const CATEGORIES = [
  { key: 'remove', label: 'Remove — it should never have happened' },
  { key: 'fix', label: 'Fix — real payment, one detail wrong' },
  { key: 'checks', label: 'Checks' },
  { key: 'charges', label: 'Closed-day charges' },
];

const PLANNED_TYPES = [
  { key: 'payment_void', cat: 'remove', label: 'Void a payment', desc: 'One unified door — replaces the two old void paths' },
  { key: 'pp_batch_void', cat: 'remove', label: 'Void a whole receipt (PP)', desc: 'Canonical whole-receipt reversal' },
  { key: 'cashout_reverse', cat: 'remove', label: 'Reverse a customer cash-out', desc: 'Multi-document reversal, off expense rows' },
  { key: 'payment_amount', cat: 'fix', label: 'Fix a payment amount', desc: 'Reversal + corrected replacement, history kept' },
  { key: 'value_date', cat: 'fix', label: 'Correct a payment date', desc: 'The P2a editor, upgraded to mint CORs' },
  { key: 'method_reclass', cat: 'fix', label: 'Reclassify payment method', desc: 'NEW — "recorded cash, was GCash"' },
  { key: 'offline_fix', cat: 'fix', label: 'Fix an offline-synced payment', desc: 'NEW — corrects payments born offline' },
  { key: 'check_bounce', cat: 'checks', label: 'Check bounce', desc: 'Bounce as COR paper, wallet + AR restored' },
  { key: 'check_unbounce', cat: 'checks', label: 'Un-bounce a check', desc: 'NEW — reverses a final bounce recorded in error' },
  { key: 'backdated_charge', cat: 'charges', label: 'Backdated interest / penalty', desc: 'NEW — dated charge onto a closed day, restatement paper minted' },
];

// Human labels for every COR type the registry can hold — the wizard's ten
// doors plus paper minted by other rooms (the receipt-fix shell).
const COR_TYPE_LABELS = {
  payment_void: 'Payment void',
  payment_amount: 'Payment amount',
  pp_batch_void: 'Whole-receipt void',
  check_bounce: 'Check bounce',
  check_unbounce: 'Check un-bounce',
  method_reclass: 'Method reclass',
  offline_fix: 'Offline fix',
  cashout_reverse: 'Cash-out reversal',
  value_date: 'Payment date',
  backdated_charge: 'Backdated charge',
  receipt_fix: 'Receipt fix',
};
const corTypeLabel = (t) => COR_TYPE_LABELS[t] || t;

function CorStatusBadge({ status }) {
  if (!status || status === 'final') return null;
  const label = status === 'rolled_back' ? 'Rolled back'
    : status === 'failed_cash_leg' ? 'Failed — no money moved'
    : status.replace(/_/g, ' ');
  return (
    <Badge variant="outline" className="text-[10px] bg-red-50 text-red-700 border-red-200">
      {label}
    </Badge>
  );
}

function TierBadge({ tier }) {
  const t = TIER_BADGE[tier] || { label: tier, cls: 'bg-gray-100 text-gray-700 border-gray-200' };
  return <Badge variant="outline" className={`text-[10px] ${t.cls}`}>{t.label}</Badge>;
}

function EvidenceBadge({ status }) {
  if (status === 'provided') return <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">Proof attached</Badge>;
  return <Badge variant="outline" className="text-[10px] bg-orange-50 text-orange-700 border-orange-200">Awaiting proof</Badge>;
}

function peso(n) {
  return `₱${Number(n || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}`;
}

// ── Registry (thin, Phase P1 — full Audit Center view arrives with P2c) ────
function RegistryTab() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`${BACKEND_URL}/api/corrections`, { params: { limit: 50 } });
      setRows(res.data.corrections || []);
      setTotal(res.data.total || 0);
    } catch (e) { /* registry stays empty on error */ }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openDetail = async (id) => {
    try {
      const res = await api.get(`${BACKEND_URL}/api/corrections/${id}`);
      setDetail(res.data);
    } catch (e) { /* noop */ }
  };

  return (
    <Card>
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
          <ListChecks size={16} /> Correction registry
          <span className="text-xs font-normal text-muted-foreground">{total} total</span>
        </CardTitle>
        <Button size="sm" variant="ghost" onClick={load} className="h-7">
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
        </Button>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <div className="text-sm text-muted-foreground py-8 text-center">
            No corrections yet. Every correction issued anywhere in the system
            will appear here with its COR number, approver and full chain.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-muted-foreground border-b">
                  <th className="py-2 pr-3">COR #</th>
                  <th className="py-2 pr-3">Type</th>
                  <th className="py-2 pr-3">Target</th>
                  <th className="py-2 pr-3">Amount</th>
                  <th className="py-2 pr-3">Tier</th>
                  <th className="py-2 pr-3">Requested / Approved</th>
                  <th className="py-2 pr-3">Proof</th>
                  <th className="py-2">Date</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b last:border-0 hover:bg-muted/40 cursor-pointer"
                      onClick={() => openDetail(r.id)} data-testid={`cor-row-${r.cor_number}`}>
                    <td className="py-2 pr-3 font-semibold whitespace-nowrap">
                      {r.cor_number} <CorStatusBadge status={r.status} />
                    </td>
                    <td className="py-2 pr-3 whitespace-nowrap">{corTypeLabel(r.type)}</td>
                    <td className="py-2 pr-3 whitespace-nowrap">{r.target?.doc_type} {r.target?.doc_number}</td>
                    <td className="py-2 pr-3 whitespace-nowrap">{peso(r.amount)}</td>
                    <td className="py-2 pr-3"><TierBadge tier={r.tier} /></td>
                    <td className="py-2 pr-3 whitespace-nowrap">
                      {r.requested_by_name}
                      <span className="text-muted-foreground"> → {r.approved_by?.name}</span>
                    </td>
                    <td className="py-2 pr-3"><EvidenceBadge status={r.evidence_status} /></td>
                    <td className="py-2 whitespace-nowrap">{r.business_date}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {detail && (
          <div className="mt-4 p-3 rounded border bg-muted/30 text-xs space-y-2" data-testid="cor-detail">
            <div className="flex items-center justify-between">
              <div className="font-semibold text-sm flex items-center gap-2">
                {detail.correction.cor_number} — {corTypeLabel(detail.correction.type)}
                <CorStatusBadge status={detail.correction.status} />
              </div>
              <Button size="sm" variant="ghost" className="h-6 text-[11px]" onClick={() => setDetail(null)}>Close</Button>
            </div>
            <div><span className="text-muted-foreground">Reason:</span> {detail.correction.reason}</div>
            {detail.correction.type === 'receipt_fix' && (
              <>
                {(detail.correction.original_snapshot?.changes || []).length > 0 && (
                  <div>
                    <span className="text-muted-foreground">What changed:</span>
                    <ul className="list-disc ml-4 mt-1 space-y-0.5">
                      {detail.correction.original_snapshot.changes.map((c, i) => <li key={i}>{c}</li>)}
                    </ul>
                  </div>
                )}
                <div className="text-muted-foreground">
                  Corrected in the receipt library — the line-by-line trail
                  lives on the receipt's own history; this COR is its
                  registry paper.
                </div>
              </>
            )}
            <div className="flex gap-4 flex-wrap">
              <span><span className="text-muted-foreground">Value date:</span> {detail.correction.value_date}</span>
              <span><span className="text-muted-foreground">Posted:</span> {detail.correction.business_date}</span>
              <span><TierBadge tier={detail.correction.tier} /></span>
            </div>
            {detail.correction.sealed_day_ref && (
              <div className="flex items-center gap-1 text-amber-800">
                <Lock size={12} /> Restates sealed day {detail.correction.sealed_day_ref.date} — the seal is untouched; this COR carries the difference.
              </div>
            )}
            {detail.cash_movements?.length > 0 && detail.cash_movements.map((m) => (
              <div key={m.id} className="flex items-center gap-1">
                <Banknote size={12} />
                Correction {m.direction === 'out' ? 'disbursement' : 'receipt'}: {peso(m.amount)} on {m.date} (its own drawer line — never an expense)
              </div>
            ))}
            {(detail.correction.reversal_refs?.length > 0 || detail.correction.replacement_refs?.length > 0) && (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-muted-foreground">Chain:</span>
                <span>{detail.correction.target?.doc_number}</span>
                {detail.correction.reversal_refs?.map((x, i) => (
                  <span key={i} className="flex items-center gap-1"><ArrowRight size={11} /> {x.doc_number} (reversal)</span>
                ))}
                {detail.correction.replacement_refs?.map((x, i) => {
                  const label = x.kind === 'settlement'
                    ? (x.absorbed_into_ar ? `charged to account ${peso(x.amount)}`
                      : x.direction === 'refund' ? `refunded ${peso(x.amount)}`
                      : `collected ${peso(x.amount)}`)
                    : (x.doc_number || x.invoice_number || null);
                  if (!label) return null; // internal trail refs (saga, edit history)
                  return (
                    <span key={i} className="flex items-center gap-1">
                      <ArrowRight size={11} /> {label}{x.kind === 'settlement' ? '' : ' (replacement)'}
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── "Flags under your management" (law A5 — fix now, prove later) ──────────
function MyFlagsTab() {
  const [flags, setFlags] = useState([]);
  const [busy, setBusy] = useState(null);   // cor id with the evidence form open
  const [note, setNote] = useState('');
  const [url, setUrl] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await api.get(`${BACKEND_URL}/api/corrections/my-flags`);
      setFlags(res.data.flags || []);
    } catch (e) { /* noop */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const attach = async (id) => {
    try {
      await api.post(`${BACKEND_URL}/api/corrections/${id}/evidence`, { note, url });
      setBusy(null); setNote(''); setUrl('');
      load();
    } catch (e) { /* keep form open */ }
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
          <Flag size={16} /> Flags under your management
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          Corrections you approved or requested that still need their story
          completed — attach proof and a short description. Unproven
          corrections age here and climb the owner's exception queue.
        </p>
      </CardHeader>
      <CardContent className="space-y-2">
        {flags.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center">
            Nothing pending — every correction under your name has its proof.
          </div>
        ) : flags.map((f) => (
          <div key={f.id} className="p-3 rounded border text-xs space-y-2" data-testid={`flag-${f.cor_number}`}>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="font-semibold">{f.cor_number} — {corTypeLabel(f.type)} — {peso(f.amount)}</div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className={`text-[10px] flex items-center gap-1 ${f.age_days > 7 ? 'bg-red-50 text-red-700 border-red-200' : 'bg-muted'}`}>
                  <Clock size={10} /> {f.age_days} day{f.age_days === 1 ? '' : 's'} without proof
                </Badge>
                <TierBadge tier={f.tier} />
              </div>
            </div>
            <div className="text-muted-foreground">{f.reason}</div>
            {busy === f.id ? (
              <div className="flex items-center gap-2 flex-wrap">
                <Input value={note} onChange={(e) => setNote(e.target.value)}
                  placeholder="What happened (short description)" className="h-7 text-xs w-64" />
                <Input value={url} onChange={(e) => setUrl(e.target.value)}
                  placeholder="Document link / upload ref (optional)" className="h-7 text-xs w-56" />
                <Button size="sm" className="h-7 text-[11px]" disabled={!note && !url} onClick={() => attach(f.id)}>
                  Save proof
                </Button>
                <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={() => setBusy(null)}>Cancel</Button>
              </div>
            ) : (
              <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => setBusy(f.id)}>
                <Upload size={11} className="mr-1" /> Attach proof
              </Button>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

// ── Wizard (Phase P2 — all nine doors live) ────────────────────────────────

const DOORS = `/api/corrections/doors`;

// Which existing permission governs each tile (owner ruling 2026-08-31:
// reuse the current permission model, never invent duplicates).
const TYPE_PERM = {
  payment_void: ['accounting', 'receive_payment'],
  payment_amount: ['accounting', 'receive_payment'],
  pp_batch_void: ['accounting', 'receive_payment'],
  check_bounce: ['accounting', 'receive_payment'],
  check_unbounce: ['accounting', 'receive_payment'],
  method_reclass: ['accounting', 'receive_payment'],
  offline_fix: ['accounting', 'receive_payment'],
  cashout_reverse: ['accounting', 'edit_expense'],
  value_date: ['accounting', 'receive_payment'],
  // The door itself enforces generate_interest OR generate_penalty per
  // charge; the tile greys on the interest perm as the common gate.
  backdated_charge: ['accounting', 'generate_interest'],
};

const BOUNCE_REASONS_UI = [
  ['daif', 'Insufficient Funds (DAIF)'],
  ['daud', 'Uncollected Deposits (DAUD)'],
  ['account_closed', 'Account Closed'],
  ['stop_payment', 'Stop Payment Order'],
  ['stale_dated', 'Stale-dated (over 6 months)'],
  ['post_dated', 'Post-dated (deposited early)'],
  ['altered', 'Altered / Tampered'],
  ['signature_mismatch', 'Signature Mismatch'],
  ['payee_mismatch', 'Payee Name Mismatch'],
  ['amount_mismatch', 'Words vs. Figures Mismatch'],
];

function doorError(e) {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (d?.type === 'insufficient_funds') return d.message || 'Not enough cash in the drawer for this correction.';
  if (d?.error === 'pin_required' || d?.tier) return d.message || 'Approval PIN required.';
  if (d?.message) return d.message;
  return 'Correction failed — nothing was changed.';
}

function FieldLabel({ children }) {
  return <div className="text-[11px] font-medium text-muted-foreground mb-1">{children}</div>;
}

function livePayments(inv) {
  return (inv?.payments || []).filter(
    (p) => !p.voided && Number(p.amount) > 0 && p.fund_source !== 'discount'
        && p.fund_source !== 'check_reversal');
}

// Finder: fuzzy receipt search (same engine as Ctrl+K — number fragment,
// customer name, or scanned doc code) → its live payment rows → pick one.
function InvoicePaymentFinder({ initialNumber = '', onPick }) {
  const [num, setNum] = useState(initialNumber);
  const [hits, setHits] = useState([]);
  const [searching, setSearching] = useState(false);
  const [settled, setSettled] = useState(false); // a suggest pass finished for the current text
  const [inv, setInv] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const debounceRef = useRef(null);

  // Exact load once a receipt is chosen (or deep-linked / typed in full).
  const search = useCallback(async (n) => {
    const target = (n ?? num).trim();
    if (!target) return;
    setBusy(true); setErr(''); setInv(null); setHits([]);
    try {
      const res = await api.get(`${BACKEND_URL}/api/invoices/by-number/${encodeURIComponent(target)}`);
      setInv(res.data);
      setNum(res.data.invoice_number || target);
    } catch (e) {
      setErr('No receipt found with that number.');
    }
    setBusy(false);
  }, [num]);

  const suggest = useCallback(async (term) => {
    const t = term.trim();
    if (t.length < 2) { setHits([]); setSearching(false); return; }
    setSearching(true);
    try {
      const res = await api.get(`${BACKEND_URL}/api/search/transactions`,
        { params: { q: t, type: 'invoice', limit: 8 } });
      setHits(res.data.results || []);
    } catch (e) { setHits([]); }
    setSearching(false);
    setSettled(true);
  }, []);

  const onType = (val) => {
    setNum(val); setInv(null); setErr(''); setSettled(false);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => suggest(val), 300);
  };

  useEffect(() => { if (initialNumber) search(initialNumber); /* deep link */ }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => clearTimeout(debounceRef.current), []);

  return (
    <div className="space-y-2">
      <FieldLabel>Find the receipt — number, part of it, customer name, or scan</FieldLabel>
      <div className="flex gap-2 items-center">
        <div className="relative w-72 max-w-full">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={num} onChange={(e) => onType(e.target.value)}
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return;
              e.preventDefault();
              if (hits.length > 0) search(hits[0].number); else search();
            }}
            placeholder="e.g. 1538, OS-MB-001538 or Dela Cruz" className="h-8 text-xs pl-8"
            data-testid="wizard-find-invoice" />
        </div>
        {(searching || busy) && <Loader2 size={14} className="animate-spin text-muted-foreground" />}
      </div>
      {hits.length > 0 && !inv && (
        <div className="rounded border divide-y" data-testid="wizard-invoice-suggestions">
          {hits.map((h) => (
            <button key={h.id} type="button"
              className="w-full text-left px-2 py-1.5 hover:bg-muted/50 text-xs flex items-center justify-between gap-2"
              onClick={() => search(h.number)} data-testid={`wizard-suggest-${h.number}`}>
              <span className="min-w-0 truncate">
                <span className="font-semibold">{h.number}</span>
                <span className="text-muted-foreground"> · {h.title || 'Walk-in'} · {h.date}</span>
              </span>
              <span className="shrink-0 text-muted-foreground">{peso(h.amount)}</span>
            </button>
          ))}
        </div>
      )}
      {settled && num.trim().length >= 2 && !searching && !busy && !inv && !err && hits.length === 0 && (
        <div className="text-xs text-muted-foreground">No receipts match that — try fewer characters, or the customer's name.</div>
      )}
      {err && <div className="text-xs text-red-600">{err}</div>}
      {inv && (
        <div className="rounded border p-2 text-xs space-y-1">
          <div className="font-semibold">{inv.invoice_number} — {inv.customer_name || 'Walk-in'} — {peso(inv.grand_total)} ({inv.status})</div>
          {livePayments(inv).length === 0 ? (
            <div className="text-muted-foreground">No live payment rows on this receipt.</div>
          ) : livePayments(inv).map((p) => (
            <button key={p.id} type="button"
              className="w-full text-left p-2 rounded border hover:bg-muted/50 flex items-center justify-between"
              onClick={() => onPick(inv, p)} data-testid={`wizard-pick-payment-${p.id}`}>
              <span>{peso(p.amount)} · {p.method || 'Cash'} · {p.intended_date || p.date}</span>
              <ArrowRight size={12} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Finder: checks by status list, with a quick filter box.
function CheckFinder({ statuses, onPick }) {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState('');
  useEffect(() => {
    (async () => {
      try {
        const lists = await Promise.all(statuses.map((s) =>
          api.get(`${BACKEND_URL}/api/checks`, { params: { status: s, limit: 50 } })));
        setRows(lists.flatMap((r) => r.data.checks || r.data.items || r.data || []));
      } catch (e) { setRows([]); }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (rows === null) return <div className="text-xs text-muted-foreground">Loading checks…</div>;
  if (!rows.length) return <div className="text-xs text-muted-foreground">No checks in {statuses.join(' / ')}.</div>;
  const needle = q.trim().toLowerCase();
  const shown = needle
    ? rows.filter((c) => `${c.check_number || ''} ${c.customer_name || ''} ${c.bank_name || c.bank || ''} ${c.amount || ''}`
        .toLowerCase().includes(needle))
    : rows;
  return (
    <div className="space-y-1">
      <div className="relative w-72 max-w-full mb-2">
        <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Filter — check #, customer, bank or amount" className="h-8 text-xs pl-8"
          data-testid="wizard-filter-checks" />
      </div>
      {shown.length === 0 && (
        <div className="text-xs text-muted-foreground">No checks match that filter.</div>
      )}
      {shown.map((c) => (
        <button key={c.id} type="button"
          className="w-full text-left p-2 rounded border hover:bg-muted/50 text-xs flex items-center justify-between"
          onClick={() => onPick(c)} data-testid={`wizard-pick-check-${c.id}`}>
          <span>#{c.check_number} · {c.customer_name} · {peso(c.amount)} · {c.status}</span>
          <ArrowRight size={12} />
        </button>
      ))}
    </div>
  );
}

// Finder: customer cash-out expenses.
function CashoutFinder({ onPick }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`${BACKEND_URL}/api/expenses`, {
          params: { category: 'Customer Cash-out', limit: 50 } });
        const list = (res.data.expenses || res.data.items || res.data || [])
          .filter((x) => !x.voided);
        setRows(list);
      } catch (e) { setRows([]); }
    })();
  }, []);
  if (rows === null) return <div className="text-xs text-muted-foreground">Loading cash-outs…</div>;
  if (!rows.length) return <div className="text-xs text-muted-foreground">No live customer cash-outs found.</div>;
  return (
    <div className="space-y-1">
      {rows.map((x) => (
        <button key={x.id} type="button"
          className="w-full text-left p-2 rounded border hover:bg-muted/50 text-xs flex items-center justify-between"
          onClick={() => onPick(x)} data-testid={`wizard-pick-cashout-${x.id}`}>
          <span>{x.date} · {x.customer_name} · {peso(x.amount)} · {x.description}</span>
          <ArrowRight size={12} />
        </button>
      ))}
    </div>
  );
}

// Finder: customer by name search (for charges that target an account).
function CustomerFinder({ onPick }) {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const search = async () => {
    if (!q.trim()) return;
    setBusy(true);
    try {
      const res = await api.get(`${BACKEND_URL}/api/customers`, {
        params: { search: q.trim(), limit: 8 } });
      setRows(res.data.customers || res.data || []);
    } catch (e) { setRows([]); }
    setBusy(false);
  };
  return (
    <div className="space-y-2">
      <FieldLabel>Customer</FieldLabel>
      <div className="flex gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && search()}
          placeholder="Customer name" className="h-8 text-xs w-56"
          data-testid="wizard-find-customer" />
        <Button size="sm" className="h-8 text-xs" disabled={busy} onClick={search}>Find</Button>
      </div>
      {rows !== null && !rows.length && (
        <div className="text-xs text-muted-foreground">No customer matches that name.</div>
      )}
      {(rows || []).map((c) => (
        <button key={c.id} type="button"
          className="w-full text-left p-2 rounded border hover:bg-muted/50 text-xs flex items-center justify-between"
          onClick={() => onPick(c)} data-testid={`wizard-pick-customer-${c.id}`}>
          <span>{c.name} · balance {peso(c.balance)}</span>
          <ArrowRight size={12} />
        </button>
      ))}
    </div>
  );
}

// Finder: open offline reconciliation entries.
function OfflineFinder({ onPick }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`${BACKEND_URL}/api/sync/offline-reconciliation`, {
          params: { status: 'open' } });
        setRows(res.data.items || []);
      } catch (e) { setRows([]); }
    })();
  }, []);
  if (rows === null) return <div className="text-xs text-muted-foreground">Loading queue…</div>;
  if (!rows.length) return <div className="text-xs text-muted-foreground">No open offline entries — the queue is clean.</div>;
  return (
    <div className="space-y-1">
      {rows.map((r) => (
        <button key={r.id} type="button"
          className="w-full text-left p-2 rounded border hover:bg-muted/50 text-xs flex items-center justify-between"
          onClick={() => onPick(r)} data-testid={`wizard-pick-offline-${r.id}`}>
          <span>{(r.created_at || '').slice(0, 10)} · {r.customer_name || '—'} · {peso(r.amount)} · {r.reason}
            {r.canonical_invoice_number ? ` · ${r.canonical_invoice_number}` : ''}</span>
          <ArrowRight size={12} />
        </button>
      ))}
    </div>
  );
}

function ReasonPinSubmit({ reason, setReason, pin, setPin, onSubmit, busy, label = 'Mint correction', disabled }) {
  return (
    <div className="space-y-2">
      <div>
        <FieldLabel>Reason (goes on the COR, word for word)</FieldLabel>
        <Input value={reason} onChange={(e) => setReason(e.target.value)}
          placeholder="What actually happened" className="h-8 text-xs" data-testid="wizard-reason" />
      </div>
      <div>
        <FieldLabel>Approval PIN</FieldLabel>
        <Input type="password" value={pin} onChange={(e) => setPin(e.target.value)}
          placeholder="Manager / admin PIN" className="h-8 text-xs w-40" data-testid="wizard-pin" />
      </div>
      <Button size="sm" className="h-8 text-xs" disabled={busy || disabled || reason.trim().length < 4 || !pin}
        onClick={onSubmit} data-testid="wizard-submit">
        {busy ? 'Working…' : label}
      </Button>
    </div>
  );
}

function SuccessPanel({ done, onAgain }) {
  return (
    <div className="rounded border border-emerald-200 bg-emerald-50 p-3 text-xs space-y-2"
      data-testid="wizard-success">
      <div className="font-semibold text-sm text-emerald-900 flex items-center gap-1">
        <ShieldCheck size={14} /> {done.cor?.cor_number || 'Done'}
      </div>
      <div className="text-emerald-900">{done.message}</div>
      {done.cor && (
        <div className="text-emerald-800">
          Tier: <TierBadge tier={done.cor.tier} /> · Amount: {peso(done.cor.amount)} —
          it is now in the Registry with its full chain.
        </div>
      )}
      <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={onAgain}>
        Another correction
      </Button>
    </div>
  );
}

function WizardForm({ type, onDone, initialInvoice, initialPp }) {
  const [target, setTarget] = useState(null);        // {invoice, payment} | check | expense | rec
  const [reason, setReason] = useState('');
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  // type-specific
  const [newAmount, setNewAmount] = useState('');
  const [newMethod, setNewMethod] = useState('GCash');
  const [newDate, setNewDate] = useState('');
  const [ppNumber, setPpNumber] = useState(initialPp || '');
  const [bounceReason, setBounceReason] = useState('daif');
  const [canRedeposit, setCanRedeposit] = useState(true);
  const [fundSource, setFundSource] = useState('bank');
  const [offAction, setOffAction] = useState('note_only');

  const post = async (path, body) => {
    setBusy(true);
    try {
      const res = await api.post(`${BACKEND_URL}${DOORS}/${path}`, body);
      onDone({ cor: res.data.cor, message: res.data.message });
    } catch (e) {
      toast.error(doorError(e));
    }
    setBusy(false);
  };

  const needsRow = ['payment_void', 'payment_amount', 'method_reclass', 'value_date'].includes(type);

  if (needsRow && !target) {
    return <InvoicePaymentFinder initialNumber={initialInvoice} onPick={(invoice, payment) => setTarget({ invoice, payment })} />;
  }

  if (needsRow) {
    const { invoice, payment } = target;
    const picked = (
      <div className="text-xs rounded border p-2 bg-muted/30">
        <span className="font-semibold">{invoice.invoice_number}</span> · {peso(payment.amount)} · {payment.method || 'Cash'} · {payment.intended_date || payment.date}
        <Button size="sm" variant="ghost" className="h-6 text-[10px] ml-2" onClick={() => setTarget(null)}>change</Button>
      </div>
    );
    const base = { invoice_id: invoice.id, payment_id: payment.id, reason, pin };
    return (
      <div className="space-y-3">
        {picked}
        {type === 'payment_amount' && (
          <div>
            <FieldLabel>Correct amount (what was actually received)</FieldLabel>
            <Input type="number" value={newAmount} onChange={(e) => setNewAmount(e.target.value)}
              className="h-8 text-xs w-40" data-testid="wizard-new-amount" />
          </div>
        )}
        {type === 'method_reclass' && (
          <div>
            <FieldLabel>The money actually arrived via</FieldLabel>
            <select value={newMethod} onChange={(e) => setNewMethod(e.target.value)}
              className="h-8 text-xs border rounded px-2" data-testid="wizard-new-method">
              <option>Cash</option><option>GCash</option><option>Maya</option>
              <option value="Bank Transfer">Bank transfer</option>
            </select>
          </div>
        )}
        {type === 'value_date' && (
          <div>
            <FieldLabel>The date the customer actually paid</FieldLabel>
            <Input type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)}
              className="h-8 text-xs w-44" data-testid="wizard-new-date" />
          </div>
        )}
        <ReasonPinSubmit {...{ reason, setReason, pin, setPin, busy }}
          disabled={(type === 'payment_amount' && !(Number(newAmount) >= 0))
                 || (type === 'value_date' && !newDate)}
          onSubmit={() => {
            if (type === 'payment_void') post('void-payment', base);
            else if (type === 'payment_amount') post('modify-payment-amount', { ...base, new_amount: Number(newAmount) });
            else if (type === 'method_reclass') post('method-reclass', {
              ...base, new_method: newMethod,
              ...(newMethod === 'Bank Transfer' ? { new_fund_source: 'bank' } : {}) });
            else post('value-date', { ...base, new_date: newDate });
          }} />
      </div>
    );
  }

  if (type === 'pp_batch_void') {
    return (
      <div className="space-y-3">
        <div>
          <FieldLabel>PP receipt number (voids the WHOLE payment event, all its receipts)</FieldLabel>
          <Input value={ppNumber} onChange={(e) => setPpNumber(e.target.value)}
            placeholder="e.g. PP-000123" className="h-8 text-xs w-56" data-testid="wizard-pp-number" />
        </div>
        <ReasonPinSubmit {...{ reason, setReason, pin, setPin, busy }} disabled={!ppNumber.trim()}
          label="Void whole receipt"
          onSubmit={() => post('void-pp', { pp_number: ppNumber.trim(), reason, pin })} />
      </div>
    );
  }

  if (type === 'check_bounce') {
    if (!target) return <CheckFinder statuses={['active', 'bounced_redepositable']} onPick={setTarget} />;
    return (
      <div className="space-y-3">
        <div className="text-xs rounded border p-2 bg-muted/30">
          Check <span className="font-semibold">#{target.check_number}</span> · {target.customer_name} · {peso(target.amount)}
          <Button size="sm" variant="ghost" className="h-6 text-[10px] ml-2" onClick={() => setTarget(null)}>change</Button>
        </div>
        <div>
          <FieldLabel>Bank's reason</FieldLabel>
          <select value={bounceReason} onChange={(e) => setBounceReason(e.target.value)}
            className="h-8 text-xs border rounded px-2" data-testid="wizard-bounce-reason">
            {BOUNCE_REASONS_UI.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </div>
        <label className="flex items-center gap-2 text-xs">
          <input type="checkbox" checked={canRedeposit} onChange={(e) => setCanRedeposit(e.target.checked)} />
          Bank allows re-deposit (uncheck = FINAL bounce, money reversed now)
        </label>
        {!canRedeposit && (
          <div>
            <FieldLabel>Reverse the money from</FieldLabel>
            <select value={fundSource} onChange={(e) => setFundSource(e.target.value)}
              className="h-8 text-xs border rounded px-2">
              <option value="bank">Bank wallet (check was deposited)</option>
              <option value="cashier">Drawer (check never left the till)</option>
            </select>
          </div>
        )}
        <ReasonPinSubmit {...{ reason, setReason, pin, setPin, busy }} label="Record bounce"
          onSubmit={() => post('check-bounce', {
            check_id: target.id, reason_code: bounceReason,
            can_redeposit: canRedeposit, reversal_fund_source: fundSource, reason, pin })} />
      </div>
    );
  }

  if (type === 'check_unbounce') {
    if (!target) return <CheckFinder statuses={['bounced_final']} onPick={setTarget} />;
    return (
      <div className="space-y-3">
        <div className="text-xs rounded border p-2 bg-muted/30">
          Check <span className="font-semibold">#{target.check_number}</span> · {target.customer_name} · {peso(target.amount)} · bounced final
          <Button size="sm" variant="ghost" className="h-6 text-[10px] ml-2" onClick={() => setTarget(null)}>change</Button>
        </div>
        <ReasonPinSubmit {...{ reason, setReason, pin, setPin, busy }} label="Reverse the bounce"
          onSubmit={() => post('check-unbounce', { check_id: target.id, reason, pin })} />
      </div>
    );
  }

  if (type === 'cashout_reverse') {
    if (!target) return <CashoutFinder onPick={setTarget} />;
    return (
      <div className="space-y-3">
        <div className="text-xs rounded border p-2 bg-muted/30">
          {target.date} · {target.customer_name} · {peso(target.amount)}
          <Button size="sm" variant="ghost" className="h-6 text-[10px] ml-2" onClick={() => setTarget(null)}>change</Button>
        </div>
        <ReasonPinSubmit {...{ reason, setReason, pin, setPin, busy }} label="Reverse cash-out"
          onSubmit={() => post('cashout-reverse', { expense_id: target.id, reason, pin })} />
      </div>
    );
  }

  if (type === 'backdated_charge') {
    if (!target) return <CustomerFinder onPick={setTarget} />;
    return (
      <div className="space-y-3">
        <div className="text-xs rounded border p-2 bg-muted/30">
          {target.name} · balance {peso(target.balance)}
          <Button size="sm" variant="ghost" className="h-6 text-[10px] ml-2" onClick={() => setTarget(null)}>change</Button>
        </div>
        <div className="flex gap-3 flex-wrap">
          <div>
            <FieldLabel>Charge</FieldLabel>
            <select value={offAction === 'penalty' ? 'penalty' : 'interest'}
              onChange={(e) => setOffAction(e.target.value)}
              className="h-8 text-xs border rounded px-2" data-testid="wizard-charge-type">
              <option value="interest">Interest</option>
              <option value="penalty">Penalty</option>
            </select>
          </div>
          <div>
            <FieldLabel>Dated (the day it belongs to)</FieldLabel>
            <Input type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)}
              className="h-8 text-xs w-44" data-testid="wizard-charge-date" />
          </div>
          {offAction !== 'penalty' && (
            <div>
              <FieldLabel>Amount (blank = computed accrual)</FieldLabel>
              <Input type="number" value={newAmount} onChange={(e) => setNewAmount(e.target.value)}
                placeholder="auto" className="h-8 text-xs w-32" />
            </div>
          )}
        </div>
        <p className="text-[11px] text-muted-foreground">
          A charge dated onto a CLOSED day mints a restatement COR; an open
          day passes through as a normal charge, no COR burned.
        </p>
        <ReasonPinSubmit {...{ reason, setReason, pin, setPin, busy }}
          disabled={!newDate} label="Create charge"
          onSubmit={() => {
            const body = {
              customer_id: target.id,
              charge: offAction === 'penalty' ? 'penalty' : 'interest',
              as_of_date: newDate, reason, pin, force: true,
            };
            if (offAction !== 'penalty' && Number(newAmount) > 0) body.manual_amount = Number(newAmount);
            post('backdated-charge', body);
          }} />
      </div>
    );
  }

  if (type === 'offline_fix') {
    if (!target) return <OfflineFinder onPick={setTarget} />;
    const sub = { rec_id: target.id, action: offAction, note: reason, reason, pin };
    return (
      <div className="space-y-3">
        <div className="text-xs rounded border p-2 bg-muted/30">
          Offline entry · {target.customer_name || '—'} · {peso(target.amount)} · {target.reason}
          <Button size="sm" variant="ghost" className="h-6 text-[10px] ml-2" onClick={() => setTarget(null)}>change</Button>
        </div>
        <div>
          <FieldLabel>What does it need?</FieldLabel>
          <select value={offAction} onChange={(e) => setOffAction(e.target.value)}
            className="h-8 text-xs border rounded px-2" data-testid="wizard-offline-action">
            <option value="note_only">Nothing — sync was fine (note only)</option>
            <option value="void_payment">Void a payment on it</option>
            <option value="modify_amount">Fix a payment amount</option>
            <option value="method_reclass">Reclassify the method</option>
          </select>
        </div>
        {offAction !== 'note_only' && !target.pickedRow && (
          <InvoicePaymentFinder initialNumber={target.canonical_invoice_number || ''}
            onPick={(invoice, payment) => setTarget({ ...target, pickedRow: { invoice, payment } })} />
        )}
        {offAction !== 'note_only' && target.pickedRow && (
          <div className="text-xs rounded border p-2 bg-muted/30">
            {target.pickedRow.invoice.invoice_number} · {peso(target.pickedRow.payment.amount)} · {target.pickedRow.payment.method || 'Cash'}
            <Button size="sm" variant="ghost" className="h-6 text-[10px] ml-2"
              onClick={() => setTarget({ ...target, pickedRow: null })}>change</Button>
          </div>
        )}
        {offAction === 'modify_amount' && (
          <div>
            <FieldLabel>Correct amount</FieldLabel>
            <Input type="number" value={newAmount} onChange={(e) => setNewAmount(e.target.value)}
              className="h-8 text-xs w-40" />
          </div>
        )}
        {offAction === 'method_reclass' && (
          <div>
            <FieldLabel>The money actually arrived via</FieldLabel>
            <select value={newMethod} onChange={(e) => setNewMethod(e.target.value)}
              className="h-8 text-xs border rounded px-2">
              <option>Cash</option><option>GCash</option><option>Maya</option>
              <option value="Bank Transfer">Bank transfer</option>
            </select>
          </div>
        )}
        <ReasonPinSubmit {...{ reason, setReason, pin, setPin, busy }} label="Resolve entry"
          disabled={offAction !== 'note_only' && !target.pickedRow}
          onSubmit={() => {
            const row = target.pickedRow;
            const extra = row ? { invoice_id: row.invoice.id, payment_id: row.payment.id } : {};
            if (offAction === 'modify_amount') extra.new_amount = Number(newAmount);
            if (offAction === 'method_reclass') {
              extra.new_method = newMethod;
              if (newMethod === 'Bank Transfer') extra.new_fund_source = 'bank';
            }
            post('offline-fix', { ...sub, ...extra });
          }} />
      </div>
    );
  }

  return null;
}

function WizardTab() {
  const [params] = useSearchParams();
  const { hasPerm } = useAuth();
  const [type, setType] = useState(() => {
    const t = params.get('type');
    return PLANNED_TYPES.some((x) => x.key === t) ? t : null;
  });
  const [done, setDone] = useState(null);
  const initialInvoice = params.get('invoice') || '';
  const meta = PLANNED_TYPES.find((t) => t.key === type);

  const allowed = (key) => {
    const [m, a] = TYPE_PERM[key] || [];
    return m ? hasPerm(m, a) : true;
  };

  if (done) {
    return (
      <Card><CardContent className="pt-4">
        <SuccessPanel done={done} onAgain={() => { setDone(null); setType(null); }} />
      </CardContent></Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
          <FilePenLine size={16} /> {meta ? meta.label : 'New correction'}
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          {meta ? meta.desc : (
            'Pick what went wrong → find the document → PIN → a numbered COR. ' +
            'Fast to do, impossible to hide: every correction is recorded, versioned and counted.')}
        </p>
      </CardHeader>
      <CardContent>
        {!type ? (
          <div className="space-y-4">
            {CATEGORIES.map((c) => (
              <div key={c.key} data-testid={`wizard-cat-${c.key}`}>
                <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">
                  {c.label}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {PLANNED_TYPES.filter((t) => t.cat === c.key).map((t) => {
                    const ok = allowed(t.key);
                    return (
                      <button key={t.key} type="button"
                        className={`p-3 rounded border text-left transition ${ok
                          ? 'bg-background hover:border-primary hover:bg-muted/40 cursor-pointer'
                          : 'bg-muted/30 opacity-60 cursor-not-allowed'}`}
                        onClick={() => ok ? setType(t.key)
                          : toast.error('Access to this feature is not allowed — ask an admin to grant it in Permission Settings.')}
                        data-testid={`wizard-type-${t.key}`}>
                        <div className="text-sm font-semibold flex items-center justify-between">
                          {t.label}
                          {!ok && <Lock size={12} className="text-muted-foreground" />}
                        </div>
                        <div className="text-[11px] text-muted-foreground mt-1">{t.desc}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            <Button size="sm" variant="ghost" className="h-7 text-[11px] -ml-2" onClick={() => setType(null)}>
              ← All correction types
            </Button>
            <WizardForm type={type} initialInvoice={initialInvoice}
              initialPp={params.get('pp') || ''} onDone={setDone} key={type} />
          </div>
        )}
        <div className="mt-3 text-[11px] text-muted-foreground flex items-center gap-1">
          <ShieldCheck size={12} />
          Every door mints a numbered COR — tiered PIN, journaled cash legs,
          sealed-day restatements. Nothing here erases; everything corrects.
        </div>
      </CardContent>
    </Card>
  );
}

export default function CorrectionCenterPage() {
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const tab = params.get('tab') || 'registry';
  const setTab = (v) => setParams((p) => { const n = new URLSearchParams(p); n.set('tab', v); return n; }, { replace: true });
  const isManagerial = useMemo(
    () => ['admin', 'owner', 'manager'].includes(user?.role), [user]);

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="correction-center-page">
      <div>
        <h1 className="text-xl font-bold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
          <FilePenLine size={20} /> Correction Center
        </h1>
        <p className="text-xs text-muted-foreground mt-1">
          New paper, never eraser — corrections happen here, as numbered COR
          documents. The Audit Center reads; this room writes.
        </p>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="registry" data-testid="tab-registry">Registry</TabsTrigger>
          <TabsTrigger value="new" data-testid="tab-new">New correction</TabsTrigger>
          {isManagerial && <TabsTrigger value="flags" data-testid="tab-flags">My flags</TabsTrigger>}
        </TabsList>
        <TabsContent value="registry"><RegistryTab /></TabsContent>
        <TabsContent value="new"><WizardTab /></TabsContent>
        {isManagerial && <TabsContent value="flags"><MyFlagsTab /></TabsContent>}
      </Tabs>
    </div>
  );
}
